/**
 * client/render/texarray.ts — builds WebGL2 2D texture arrays straight from decoded images (ImageBitmap /
 * HTMLImageElement / canvas), without a CPU pixel readback. OWNER: RENDER agent.
 *
 * Why: the map has 662 materials (415 distinct texture sets). Packing every texture of the same size into
 * one array lets all world surfaces and static models that share a lighting/alpha state draw in ONE call
 * (a per-vertex layer index picks the texture), which is how we get from ~1086 draw calls to ~100.
 *
 * How: three allocates the immutable storage (texStorage3D with a full mip chain; `source.dataReady=false`
 * skips three's own upload), then we texSubImage3D each layer from the image and generate mipmaps once.
 * Three's binding cache is respected by binding through `renderer.state`.
 */
import * as THREE from 'three';

export type LayerSource = TexImageSource | { flat: [number, number, number, number] };

export class TextureArrayBuilder {
  readonly layers: LayerSource[] = [];
  private readonly index = new Map<unknown, number>();

  constructor(readonly width: number, readonly height: number) {}

  /** Returns the layer index for `key` (dedupes), adding `src` if new. */
  add(key: unknown, src: LayerSource): number {
    const hit = this.index.get(key);
    if (hit !== undefined) return hit;
    const i = this.layers.length;
    this.layers.push(src);
    this.index.set(key, i);
    return i;
  }

  get count(): number { return this.layers.length; }

  /** Upload to the GPU. `srgb` is informational only: we always sample raw values (the shaders square them). */
  build(renderer: THREE.WebGLRenderer, anisotropy: number): THREE.DataArrayTexture {
    const w = this.width, h = this.height, n = Math.max(1, this.layers.length);
    const tex = new THREE.DataArrayTexture(null, w, h, n);
    tex.format = THREE.RGBAFormat;
    tex.type = THREE.UnsignedByteType;
    tex.colorSpace = THREE.NoColorSpace;
    tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
    tex.magFilter = THREE.LinearFilter;
    tex.minFilter = THREE.LinearMipmapLinearFilter;
    tex.generateMipmaps = true; // makes three allocate the full mip chain (we fill + generate ourselves)
    tex.anisotropy = anisotropy;
    tex.unpackAlignment = 1;
    tex.source.dataReady = false; // allocate only
    tex.needsUpdate = true; // version 1 -> initTexture runs uploadTexture (texStorage3D)
    renderer.initTexture(tex);
    const gl = renderer.getContext() as WebGL2RenderingContext;
    const glTex = (renderer.properties.get(tex) as { __webglTexture?: WebGLTexture }).__webglTexture;
    if (!glTex) throw new Error('texture array allocation failed');
    renderer.state.bindTexture(gl.TEXTURE_2D_ARRAY, glTex);
    gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, false);
    gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, false);
    gl.pixelStorei(gl.UNPACK_COLORSPACE_CONVERSION_WEBGL, gl.NONE);
    gl.pixelStorei(gl.UNPACK_ALIGNMENT, 1);
    let flatBuf: Uint8Array | null = null;
    for (let i = 0; i < this.layers.length; i++) {
      const src = this.layers[i];
      if ('flat' in src) {
        if (!flatBuf) flatBuf = new Uint8Array(w * h * 4);
        const [r, g, b, a] = src.flat;
        for (let k = 0; k < flatBuf.length; k += 4) { flatBuf[k] = r; flatBuf[k + 1] = g; flatBuf[k + 2] = b; flatBuf[k + 3] = a; }
        gl.texSubImage3D(gl.TEXTURE_2D_ARRAY, 0, 0, 0, i, w, h, 1, gl.RGBA, gl.UNSIGNED_BYTE, flatBuf);
      } else {
        gl.texSubImage3D(gl.TEXTURE_2D_ARRAY, 0, 0, 0, i, w, h, 1, gl.RGBA, gl.UNSIGNED_BYTE, src);
      }
    }
    gl.generateMipmap(gl.TEXTURE_2D_ARRAY);
    renderer.state.unbindTexture();
    this.layers.length = 0;
    this.index.clear();
    return tex;
  }
}

/** Size of a decoded image source. */
export function imageSize(img: unknown): [number, number] {
  const i = img as { width?: number; height?: number; naturalWidth?: number; naturalHeight?: number };
  return [i.naturalWidth || i.width || 0, i.naturalHeight || i.height || 0];
}

/** Resample an image to (w, h) — GPU-side via createImageBitmap when available. */
export async function resizeImage(img: TexImageSource, w: number, h: number): Promise<TexImageSource> {
  const [sw, sh] = imageSize(img);
  if (sw === w && sh === h) return img;
  if (typeof createImageBitmap === 'function') {
    return createImageBitmap(img as ImageBitmapSource, { resizeWidth: w, resizeHeight: h, resizeQuality: 'high', premultiplyAlpha: 'none', colorSpaceConversion: 'none' });
  }
  const c = document.createElement('canvas');
  c.width = w; c.height = h;
  c.getContext('2d')!.drawImage(img as CanvasImageSource, 0, 0, w, h);
  return c;
}
